import React, { useState, useMemo } from 'react';
import { useTheme } from '../../../context/ThemeContext';
import Plot from 'react-plotly.js';
import { Data, Layout } from 'plotly.js';
// @ts-ignore
import { Helmet } from 'react-helmet';

interface FunctionData {
  id: string;
  equation: string;
  color: string;
}

const COLORS = [
  '#3B82F6', // blue-500
  '#10B981', // emerald-500
  '#F59E0B', // amber-500
  '#EF4444', // red-500
  '#8B5CF6', // violet-500
  '#EC4899', // pink-500
];

const GraphPage: React.FC = () => {
  const { isDarkMode } = useTheme();
  const [functions, setFunctions] = useState<FunctionData[]>([
    { id: '1', equation: 'x^2', color: COLORS[0] }
  ]);
  const [error, setError] = useState<string>('');

  // Generate points for all functions
  const graphData = useMemo(() => {
    const traces = functions.map(func => {
      const x: number[] = [];
      const y: number[] = [];
      const step = 0.2;
      const range = 20;

      for (let i = -range; i <= range; i += step) {
        try {
          const processedEquation = func.equation
            .replace(/x\^(\d+)/g, 'Math.pow(x, $1)')
            .replace(/sin\(/g, 'Math.sin(')
            .replace(/cos\(/g, 'Math.cos(')
            .replace(/tan\(/g, 'Math.tan(')
            .replace(/sqrt\(/g, 'Math.sqrt(')
            .replace(/ln\(/g, 'Math.log(')
            .replace(/log\(/g, 'Math.log10(')
            .replace(/e\^/g, 'Math.exp(')
            .replace(/x/g, i.toString());
          
          const yValue = eval(processedEquation);
          if (!isNaN(yValue) && isFinite(yValue)) {
            x.push(i);
            y.push(yValue);
          }
        } catch (err) {
          setError('Invalid equation');
        }
      }

      return {
        x,
        y,
        type: 'scatter' as const,
        mode: 'lines' as const,
        name: `f(x) = ${func.equation}`,
        line: {
          color: func.color,
          width: 2
        }
      };
    });

    return traces;
  }, [functions]);

  const handleAddFunction = () => {
    const newId = (functions.length + 1).toString();
    setFunctions([...functions, { 
      id: newId, 
      equation: '', 
      color: COLORS[functions.length % COLORS.length] 
    }]);
  };

  const handleRemoveFunction = (id: string) => {
    setFunctions(functions.filter(f => f.id !== id));
  };

  const handleEquationChange = (id: string, equation: string) => {
    setFunctions(functions.map(f => 
      f.id === id ? { ...f, equation } : f
    ));
    setError('');
  };

  return (
    <>
      <Helmet>
        <title>Graph Plotter | Plot Mathematical Functions Online - Davinqi</title>
        <meta name="description" content="Plot mathematical functions and equations online with Davinqi Graph Plotter. Visualize, analyze, and share your graphs easily." />
        <meta property="og:title" content="Graph Plotter | Plot Mathematical Functions Online - Davinqi" />
        <meta property="og:description" content="Plot mathematical functions and equations online with Davinqi Graph Plotter. Visualize, analyze, and share your graphs easily." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://davinqi.com/math/graph" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Graph Plotter | Plot Mathematical Functions Online - Davinqi" />
        <meta name="twitter:description" content="Plot mathematical functions and equations online with Davinqi Graph Plotter. Visualize, analyze, and share your graphs easily." />
      </Helmet>
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-4">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
              Function Grapher
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              Plot mathematical functions with ease
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            {/* Left Panel - Function Controls */}
            <div className="lg:col-span-1">
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-4">
                <div className="space-y-4">
                  {functions.map(func => (
                    <div key={func.id} className="flex items-center gap-2">
                      <div 
                        className="w-4 h-4 rounded-full" 
                        style={{ backgroundColor: func.color }}
                      />
                      <input
                        type="text"
                        value={func.equation}
                        onChange={(e) => handleEquationChange(func.id, e.target.value)}
                        placeholder="Enter function (e.g., x^2)"
                        className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg 
                                 focus:ring-2 focus:ring-blue-500 focus:border-transparent
                                 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      />
                      {functions.length > 1 && (
                        <button
                          onClick={() => handleRemoveFunction(func.id)}
                          className="p-2 text-red-500 hover:text-red-600"
                        >
                          ×
                        </button>
                      )}
                    </div>
                  ))}
                  <button
                    onClick={handleAddFunction}
                    className="w-full px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 
                             transition-colors duration-200"
                  >
                    Add Function
                  </button>
                  {error && (
                    <p className="text-red-500 text-sm">{error}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Right Panel - Graph */}
            <div className="lg:col-span-3">
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-4">
                <div className="h-[600px] w-full">
                  <Plot
                    data={graphData}
                    layout={{
                      autosize: true,
                      margin: { l: 50, r: 50, t: 50, b: 50 },
                      xaxis: {
                        title: 'x',
                        range: [-20, 20],
                        gridcolor: isDarkMode ? '#374151' : '#E5E7EB',
                        zerolinecolor: isDarkMode ? '#E5E7EB' : '#1F2937',
                        tickcolor: isDarkMode ? '#E5E7EB' : '#1F2937',
                        color: isDarkMode ? '#E5E7EB' : '#1F2937',
                        dtick: 5
                      },
                      yaxis: {
                        title: 'y',
                        range: [-20, 20],
                        gridcolor: isDarkMode ? '#374151' : '#E5E7EB',
                        zerolinecolor: isDarkMode ? '#E5E7EB' : '#1F2937',
                        tickcolor: isDarkMode ? '#E5E7EB' : '#1F2937',
                        color: isDarkMode ? '#E5E7EB' : '#1F2937',
                        dtick: 5
                      },
                      paper_bgcolor: 'transparent',
                      plot_bgcolor: 'transparent',
                      showlegend: true,
                      legend: {
                        font: {
                          color: isDarkMode ? '#E5E7EB' : '#1F2937'
                        }
                      }
                    }}
                    config={{
                      responsive: true,
                      displayModeBar: true,
                      displaylogo: false,
                      modeBarButtonsToAdd: [],
                      modeBarButtonsToRemove: ['lasso2d', 'select2d']
                    }}
                    style={{ width: '100%', height: '100%' }}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Help Section */}
          <div className="mt-8 bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
              Supported Functions
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <h3 className="font-medium text-gray-900 dark:text-white mb-2">Basic Operations</h3>
                <ul className="text-gray-600 dark:text-gray-400 space-y-1">
                  <li>Addition: x + 2</li>
                  <li>Subtraction: x - 3</li>
                  <li>Multiplication: 2*x</li>
                  <li>Division: x/2</li>
                  <li>Power: x^2</li>
                </ul>
              </div>
              <div className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <h3 className="font-medium text-gray-900 dark:text-white mb-2">Trigonometric</h3>
                <ul className="text-gray-600 dark:text-gray-400 space-y-1">
                  <li>Sine: sin(x)</li>
                  <li>Cosine: cos(x)</li>
                  <li>Tangent: tan(x)</li>
                </ul>
              </div>
              <div className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <h3 className="font-medium text-gray-900 dark:text-white mb-2">Other Functions</h3>
                <ul className="text-gray-600 dark:text-gray-400 space-y-1">
                  <li>Square root: sqrt(x)</li>
                  <li>Natural log: ln(x)</li>
                  <li>Log base 10: log(x)</li>
                  <li>Exponential: e^x</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default GraphPage; 